import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, X, CalendarDays, List, Users } from "lucide-react";

// ---- date helpers (local timezone, YYYY-MM-DD) ----
const pad = (n) => String(n).padStart(2, "0");
const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const monthName = (m) => ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"][m];
const dayName = (i) => ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"][i];

const Leaves = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-11
  const [technicians, setTechnicians] = useState([]);
  const [leaves, setLeaves] = useState([]); // [{date, technician_ids}]
  const [openDate, setOpenDate] = useState(null); // ISO string
  const [openIds, setOpenIds] = useState([]); // selected in modal
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("calendar"); // "calendar" | "list"

  const monthStart = useMemo(() => new Date(year, month, 1), [year, month]);
  const monthEnd = useMemo(() => new Date(year, month + 1, 0), [year, month]);

  const loadTechs = () => api.get("/technicians").then((r) => setTechnicians(r.data)).catch(() => {});
  const loadLeaves = () => {
    const start = toIso(monthStart);
    const end = toIso(monthEnd);
    api.get(`/leaves?start=${start}&end=${end}`).then((r) => setLeaves(r.data)).catch(() => {});
  };
  useEffect(() => { loadTechs(); }, []);
  useEffect(() => { loadLeaves(); /* eslint-disable-next-line */ }, [year, month]);

  const leavesByDate = useMemo(() => {
    const m = {};
    leaves.forEach((l) => { m[l.date] = l.technician_ids || []; });
    return m;
  }, [leaves]);

  const techMap = useMemo(() => {
    const m = {};
    technicians.forEach((t) => { m[t.id] = t.name; });
    return m;
  }, [technicians]);

  // Build calendar grid (5-6 rows × 7 days), starting from Sunday
  const cells = useMemo(() => {
    const firstDow = monthStart.getDay(); // 0=Sun
    const lastDate = monthEnd.getDate();
    const arr = [];
    for (let i = 0; i < firstDow; i++) arr.push(null);
    for (let d = 1; d <= lastDate; d++) arr.push(new Date(year, month, d));
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [year, month, monthStart, monthEnd]);

  const openModal = (isoDate) => {
    setOpenDate(isoDate);
    setOpenIds(leavesByDate[isoDate] || []);
  };
  const toggleId = (id) => {
    setOpenIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const saveModal = async () => {
    setSaving(true);
    try {
      await api.put(`/leaves/${openDate}`, { technician_ids: openIds });
      toast.success(openIds.length === 0 ? "Data libur dihapus" : `Disimpan: ${openIds.length} karyawan libur`);
      setOpenDate(null);
      loadLeaves();
    } catch {
      toast.error("Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(year - 1); } else setMonth(month - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(year + 1); } else setMonth(month + 1); };
  const todayIso = toIso(new Date());

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="label-cap">Manajemen</div>
          <h1 className="font-heading text-4xl font-bold">Libur Karyawan</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex border border-[#E4E4E7] bg-white">
            <button data-testid="leaves-view-calendar" onClick={() => setView("calendar")} className={`px-3 py-2 text-xs font-bold flex items-center gap-1 ${view === "calendar" ? "bg-[#002FA7] text-white" : ""}`}><CalendarDays size={14} /> Kalender</button>
            <button data-testid="leaves-view-list" onClick={() => setView("list")} className={`px-3 py-2 text-xs font-bold flex items-center gap-1 ${view === "list" ? "bg-[#002FA7] text-white" : ""}`}><List size={14} /> List</button>
          </div>
          <button onClick={prevMonth} className="p-2 border border-[#E4E4E7] bg-white" data-testid="leaves-prev-month"><ChevronLeft size={16} /></button>
          <div className="px-4 py-2 border border-[#E4E4E7] bg-white text-sm font-bold min-w-[160px] text-center" data-testid="leaves-month-label">{monthName(month)} {year}</div>
          <button onClick={nextMonth} className="p-2 border border-[#E4E4E7] bg-white" data-testid="leaves-next-month"><ChevronRight size={16} /></button>
        </div>
      </div>

      {view === "calendar" && (
        <div className="bg-white border border-[#E4E4E7]" data-testid="leaves-calendar">
          <div className="grid grid-cols-7 border-b border-[#E4E4E7]">
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className={`px-3 py-2 label-cap ${i === 0 ? "text-[#DC2626]" : ""}`}>{dayName(i)}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((d, i) => {
              if (!d) return <div key={i} className="min-h-[92px] border-b border-r border-[#F4F4F5] bg-[#FAFAFA]" />;
              const iso = toIso(d);
              const libur = leavesByDate[iso] || [];
              const isToday = iso === todayIso;
              const isSun = d.getDay() === 0;
              return (
                <button
                  key={i}
                  onClick={() => openModal(iso)}
                  data-testid={`leaves-cell-${iso}`}
                  className={`min-h-[92px] border-b border-r border-[#F4F4F5] p-2 text-left hover:bg-[#F4F4F5] transition ${isToday ? "bg-[#EFF6FF]" : ""}`}
                >
                  <div className={`text-sm font-bold ${isSun ? "text-[#DC2626]" : ""}`}>{d.getDate()}</div>
                  {libur.length > 0 && (
                    <div className="mt-1">
                      <div className="inline-flex items-center gap-1 text-[10px] font-bold text-[#DC2626] mb-1"><Users size={10} /> {libur.length} libur</div>
                      <div className="text-[9px] text-[#52525B] leading-tight line-clamp-3">
                        {libur.slice(0, 3).map((id) => techMap[id] || "?").join(", ")}
                        {libur.length > 3 && ` +${libur.length - 3}`}
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {view === "list" && (
        <div className="bg-white border border-[#E4E4E7]" data-testid="leaves-list">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E4E4E7]">
                <th className="text-left label-cap px-4 py-3">Tanggal</th>
                <th className="text-left label-cap px-4 py-3">Hari</th>
                <th className="text-left label-cap px-4 py-3">Karyawan Libur</th>
                <th className="text-right label-cap px-4 py-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {leaves.length === 0 && <tr><td colSpan="4" className="p-8 text-center text-sm text-[#52525B]">Tidak ada data libur di bulan {monthName(month)} {year}.</td></tr>}
              {leaves.map((l) => {
                const d = new Date(l.date + "T00:00:00");
                const names = (l.technician_ids || []).map((id) => techMap[id] || "?").join(", ");
                return (
                  <tr key={l.date} className="border-b border-[#E4E4E7]">
                    <td className="px-4 py-3 text-sm font-mono">{l.date}</td>
                    <td className="px-4 py-3 text-sm">{dayName(d.getDay())}</td>
                    <td className="px-4 py-3 text-sm"><span className="font-bold text-[#DC2626]">{l.technician_ids.length}</span> · {names}</td>
                    <td className="px-4 py-3 text-right"><button onClick={() => openModal(l.date)} className="px-3 py-1 text-xs font-bold border border-[#E4E4E7] hover:bg-[#F4F4F5]" data-testid={`leaves-edit-${l.date}`}>Ubah</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {openDate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setOpenDate(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#E4E4E7] w-full max-w-lg" data-testid="leaves-modal">
            <div className="px-6 py-4 border-b border-[#E4E4E7] flex items-center justify-between">
              <div>
                <div className="label-cap">Tanggal</div>
                <h2 className="font-heading text-2xl font-bold">{openDate}</h2>
              </div>
              <button onClick={() => setOpenDate(null)} className="p-2 hover:bg-[#F4F4F5]"><X size={16} /></button>
            </div>
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="label-cap mb-3">Pilih karyawan yang libur di tanggal ini</div>
              {technicians.length === 0 && <div className="text-sm text-[#52525B]">Belum ada karyawan terdaftar.</div>}
              {technicians.map((t) => (
                <label key={t.id} className="flex items-center gap-3 py-2 border-b border-[#F4F4F5] cursor-pointer hover:bg-[#FAFAFA] px-2">
                  <input type="checkbox" checked={openIds.includes(t.id)} onChange={() => toggleId(t.id)} className="w-4 h-4 accent-[#DC2626]" data-testid={`leaves-tech-checkbox-${t.id}`} />
                  <div className="flex-1">
                    <div className="text-sm font-bold">{t.name}</div>
                    <div className="text-[10px] text-[#52525B] font-mono">{t.phone} · {t.base_location || "-"}{t.nik ? ` · NIK ${t.nik}` : ""}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="px-6 py-4 border-t border-[#E4E4E7] flex justify-between items-center gap-2">
              <div className="text-xs text-[#52525B]">{openIds.length} dipilih</div>
              <div className="flex gap-2">
                <button onClick={() => setOpenDate(null)} disabled={saving} className="px-4 py-2 text-xs font-bold border border-[#E4E4E7]" data-testid="leaves-cancel">Batal</button>
                <button onClick={saveModal} disabled={saving} className="px-4 py-2 text-xs font-bold bg-[#002FA7] text-white hover:bg-[#00227A] disabled:opacity-60" data-testid="leaves-save">{saving ? "Menyimpan…" : "Simpan"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaves;
